import moment from 'moment';
import { type FilterOptions } from 'src/services/placementOverview/types/filterOptions';
import { type TableDataRow } from 'src/services/placementOverview/types/tableDataRow';

const FILTER_DATE_FORMAT = 'MM/DD/YYYY';
const parsePlacementDate = (date: string) => moment(date, 'L', true);
const parseFilterDate = (date: string) => moment(date, FILTER_DATE_FORMAT, true);

class PlacementFilterService {
  public filterPlacements(placements: TableDataRow[], appliedFilters: FilterOptions): TableDataRow[] {
    const { jobs, shiftCodes, daysInCategory, jobTypes, placementStartDate, placementEndDate, sites } = appliedFilters;

    placements = this.filterPlacementsByJobs(placements, jobs);
    placements = this.filterPlacementsByShifts(placements, shiftCodes);
    placements = this.filterPlacementsByDaysInCategory(placements, daysInCategory);
    placements = this.filterPlacementsByJobTypes(placements, jobTypes);
    placements = this.filterPlacementsByStartDate(placements, placementStartDate);
    placements = this.filterPlacementsByEndDate(placements, placementEndDate);
    placements = this.filterPlacementsBySite(placements, sites);
    return placements;
  }

  private filterPlacementsByJobs(placements: TableDataRow[], jobsInFilter: string[]): TableDataRow[] {
    if (jobsInFilter.length === 0) {
      return placements;
    }
    const jobSet = new Set(jobsInFilter);
    return placements.filter(
      (placement) => placement.jobsDisplayed && placement.jobs.some((job) => job !== null && jobSet.has(job)),
    );
  }

  private filterPlacementsByShifts(placements: TableDataRow[], shiftsInFilter: string[]): TableDataRow[] {
    if (shiftsInFilter.length === 0) {
      return placements;
    }
    const shiftSet = new Set(shiftsInFilter);
    return placements.filter(
      (placement) =>
        placement.jobsDisplayed && placement.shiftCodes.some((shift) => shift !== null && shiftSet.has(shift)),
    );
  }

  private filterPlacementsByDaysInCategory(placements: TableDataRow[], daysInFilter: number[]): TableDataRow[] {
    if (daysInFilter.length === 0) {
      return placements;
    }
    const daysSet = new Set(daysInFilter);
    return placements.filter((placement) => daysSet.has(placement.daysInCategory));
  }

  public filterPlacementsByPlacementTypes(
    placements: TableDataRow[],
    placementTypesInFilter: string[],
  ): TableDataRow[] {
    if (placementTypesInFilter.length === 0) {
      return placements;
    }
    const placementTypesSet = new Set(placementTypesInFilter);
    return placements.filter(
      (placement) => placement.placementType !== null && placementTypesSet.has(placement.placementType),
    );
  }

  public filterPlacementsByRequesterLogin(
    placements: TableDataRow[],
    requesterLoginInFilter: string | null,
  ): TableDataRow[] {
    if (requesterLoginInFilter === null) {
      return placements;
    }
    // Case-insensitive like the metrics endpoint so the table and cards count the same placements
    const requesterLogin = requesterLoginInFilter.toLowerCase();
    return placements.filter((placement) => placement.requesterLogin.toLowerCase() === requesterLogin);
  }

  private filterPlacementsByJobTypes(placements: TableDataRow[], jobTypesInFilter: string[]): TableDataRow[] {
    if (jobTypesInFilter.length === 0) {
      return placements;
    }
    const jobTypesSet = new Set(jobTypesInFilter);
    return placements.filter(
      (placement) =>
        placement.jobsDisplayed && placement.jobTypes.some((jobType) => jobType !== null && jobTypesSet.has(jobType)),
    );
  }

  private filterPlacementsByStartDate(placements: TableDataRow[], startDateInFilter: string): TableDataRow[] {
    if (startDateInFilter === '') {
      return placements;
    }
    // on or after start date; unassigned placement dates do not satisfy a date constraint
    return placements.filter((placement) => {
      const placementStartDate = parsePlacementDate(placement.placementStartDate);
      return placementStartDate.isValid() && placementStartDate.isSameOrAfter(parseFilterDate(startDateInFilter));
    });
  }

  private filterPlacementsByEndDate(placements: TableDataRow[], endDateInFilter: string): TableDataRow[] {
    if (endDateInFilter === '') {
      return placements;
    }
    // on or before end date; unassigned placement dates do not satisfy a date constraint
    return placements.filter((placement) => {
      const placementEndDate = parsePlacementDate(placement.placementEndDate);
      return placementEndDate.isValid() && placementEndDate.isSameOrBefore(parseFilterDate(endDateInFilter));
    });
  }

  private filterPlacementsBySite(placements: TableDataRow[], sitesInFilter: string[]): TableDataRow[] {
    if (sitesInFilter.length === 0) {
      return placements;
    }
    const siteSet = new Set(sitesInFilter);
    return placements.filter((placement) => siteSet.has(placement.site));
  }
}

export default new PlacementFilterService();
